import React from 'react';
import { Link } from 'react-router-dom';

import { AuthShell } from '../../design-system/components/shells';
import {
  AuthFootnote,
  AuthHint,
  AuthOtp,
  AuthSubmit,
} from '../../design-system/patterns/auth';
import { routes } from '../../routes/routes';

/**
 * Admin login step 2, the view. Pure: values in, callbacks out. The container
 * (`AdminAccessCode.jsx`) owns the session handoff, the verification call, the
 * toasts and the navigation.
 *
 * WHY `AuthOtp` AND NOT THE SINGLE FIELD THIS REPLACES
 * ---------------------------------------------------
 * The old screen was one text input with `maxLength="6"` and a placeholder that
 * said "Enter 6-digit code". This is a six digit code arriving by email, which
 * is the exact shape `AuthOtp` exists for: it distributes a pasted or autofilled
 * code across the cells, carries `autocomplete="one-time-code"` on the first
 * cell so the browser and iOS can fill it, and it carries NO maxlength, which is
 * the trap the old field walked into. `maxlength` truncates before the input
 * event fires, so an autofilled six digit code arrives as one digit and the
 * other five are lost with no way for the page to know.
 *
 * NO RESEND. The app has no endpoint that re-sends an admin access code, only
 * `admin-login/verify`. A resend button that quietly did nothing, or that sent
 * the user back through step 1 without saying so, is worse than no button, so
 * the escape hatch is the back link to step 1 instead.
 *
 * The submit stays live on an incomplete code on purpose. The container's
 * validation produces "Access code must be 6 digits", and a disabled submit
 * would make that message unreachable while telling the user nothing about why
 * the button is dead.
 */

export function AdminAccessCodeView({
  email,
  values = ['', '', '', '', '', ''],
  onChange,
  onSubmit,
  onBack,
  loading = false,
  error,
}) {
  return (
    <AuthShell
      title="Enter your access code"
      subtitle={
        email ? (
          <>
            We sent a 6-digit access code to <strong>{email}</strong>.
          </>
        ) : (
          'We sent a 6-digit access code to your administrator email.'
        )
      }
      headline="Administrative access to the Sleevr marketplace."
      proofs={[
        'Two step sign in required',
        'Access code sent by email',
        'Every attempt is logged',
      ]}
      back={{ onClick: onBack, label: 'Back to admin login' }}
      footer={
        <AuthFootnote>
          Wrong account? <Link to={routes.LOGIN}>Back to sign in</Link>
          {' · '}
          <Link to={routes.MARKETPLACE}>Return to marketplace</Link>
        </AuthFootnote>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        <AuthOtp
          values={values}
          onChange={onChange}
          error={error}
          disabled={loading}
          label="6-digit administrator access code"
        />

        <AuthSubmit loading={loading} loadingLabel="Accessing">
          Access console
        </AuthSubmit>
      </form>

      <AuthHint>
        No code yet? Check your spam folder before going back a step. This is a
        restricted administrative interface and access attempts are logged and monitored.
      </AuthHint>
    </AuthShell>
  );
}

export default AdminAccessCodeView;
